import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TapIcon from '@assets/img/icons/tap.svg?react';
import cn from 'classnames';

import { AppRoute } from '@/common/app/routes';
import { getImage } from '@/common/lib/images';
import { type ResourceTypeText } from '@/common/types/resource';
import { StringSmall, StringXSmall } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';
import { selectMaterialsByResourceRecipe } from '@/redux/selectors/factoryReducer';
import { useAppSelector } from '@/redux/store';

import { EnergyButton } from '../EnergyButton/EnergyButton';

import css from './styles.module.scss';

interface MineResourceInfoProps {
  resourcePerTap: number;
  resourceName: ResourceTypeText;
}

const MineResourceInfo: FC<MineResourceInfoProps> = ({
  resourcePerTap,
  resourceName,
}) => {
  const { t } = useTranslation();
  const materialList = useAppSelector((state) =>
    selectMaterialsByResourceRecipe(state, resourceName),
  );
  const navigate = useNavigate();

  return (
    <div className="w-full flex items-center gap-2 mb-6">
      <div className={css.tap}>
        <TapIcon className="w-6 h-6 shrink-0" />
        <div className="flex flex-col">
          <StringSmall weight="bold">+{resourcePerTap}</StringSmall>
          <StringXSmall weight="regular" className="!text-white/40">
            {t('Per tap')}
          </StringXSmall>
        </div>
      </div>
      <EnergyButton />
      {materialList.length === 0 && <div className="h-12 w-[6.5rem]"></div>}
      {!!materialList.length &&
        materialList.map(({ id, materialName }) => {
          const length = materialList.length;
          const image = getImage({ name: materialName, field: 'icon' });

          return (
            <div
              key={id}
              onClick={() => {
                navigate(AppRoute.FACTORY + `/${materialName}`);
              }}
              className={cn(
                'flex items-center justify-center h-12 bg-white/10 backdrop-blur-sm',
                length !== 1
                  ? 'rounded-full w-12'
                  : 'rounded-[80px] w-[6.5rem]',
              )}
            >
              <Picture
                className="h-full aspect-square"
                img={image?.img}
                webp={image?.webp}
                alt={materialName}
              />
            </div>
          );
        })}
    </div>
  );
};

export { MineResourceInfo };
